/** @jsxImportSource @emotion/react */
import React, { useMemo } from "react";
import { DurationConstants } from "apps/RevenueEstimator/Model";
import { Country, DayPart, DaysConstants } from "model/Model";
import { formatDateAsEnGb, Selectable } from "Utilities";
import { css } from "@emotion/react";

const styles = {
  displayGroupSpace: css`
    margin-right: 5px;
  `,

  country: css`
    display: inline-block;
    width: 180px;
  `,

  warning: css`
    color: #cc0000;
  `
};

export const DateTimeDescription = ( { fromDate, toDate, duration, useDayParts, selectedCountries, fromTime, toTime, days, warningMessage }: { selectedCountries: Array<Country & { selectedDayParts: Array<Selectable<DayPart>>; }>; fromDate?: number; toDate?: number; days: Array<DaysConstants>; duration: DurationConstants; fromTime: string; toTime: string; useDayParts: boolean; warningMessage?: string; } ): JSX.Element => {
  const parts = useMemo( () => {
    const results: Array<JSX.Element> = [];

    fromDate && results.push( ( <span css={ styles.displayGroupSpace } key="1" >{ formatDateAsEnGb( fromDate ) }</span> ) );
    fromDate && toDate && results.push( ( <span css={ styles.displayGroupSpace } key="2" > - </span> ) );
    toDate && results.push( ( <span css={ styles.displayGroupSpace } key="3" >{ formatDateAsEnGb( toDate ) }</span> ) );

    const daysDescription = [];
    if ( days.length === 7 ) {
      daysDescription.push( "[Mon-Sun]" );
    } else {
      for ( const day of days ) {
        switch ( day ) {
          case DaysConstants.monday: daysDescription.push( "Mon" ); break;
          case DaysConstants.tuesday: daysDescription.push( "Tue" ); break;
          case DaysConstants.wednesday: daysDescription.push( "Wed" ); break;
          case DaysConstants.thursday: daysDescription.push( "Thu" ); break;
          case DaysConstants.friday: daysDescription.push( "Fri" ); break;
          case DaysConstants.saturday: daysDescription.push( "Sat" ); break;
          case DaysConstants.sunday: daysDescription.push( "Sun" ); break;
        }
      }
    }

    results.push( ( <span css={ styles.displayGroupSpace } key={ "days" }>{ daysDescription.join( ", " ) }</span> ) );

    switch ( duration ) {
      case DurationConstants.All: results.push( ( <span css={ styles.displayGroupSpace } key={ "minLength" }>{ "All (10+ Min)" }</span> ) ); break;
      case DurationConstants.HalfHour: results.push( ( <span css={ styles.displayGroupSpace } key={ "minLength" }>{ "½ Hour (10-39 Min)." }</span> ) ); break;
      case DurationConstants.Hour: results.push( ( <span css={ styles.displayGroupSpace } key={ "minLength" }>{ "1 Hour (40-79 Min)." }</span> ) ); break;
      case DurationConstants.HalfHourToHour: results.push( ( <span css={ styles.displayGroupSpace } key={ "minLength" }>{ "½ Hour - 1 Hour (10-79 Min)." }</span> ) ); break;
      case DurationConstants.Film80To120Minutes: results.push( ( <span css={ styles.displayGroupSpace } key={ "minLength" }>{ "Film (80-120 Min)." }</span> ) ); break;
      case DurationConstants.Film120MinutesPlus: results.push( ( <span css={ styles.displayGroupSpace } key={ "minLength" }>{ "Film (120+ Min)." }</span> ) ); break;
      case DurationConstants.Film80MinutesPlus: results.push( ( <span css={ styles.displayGroupSpace } key={ "minLength" }>{ "Film (80+ Min)." }</span> ) ); break;
    }

    if ( useDayParts ) {
      if ( selectedCountries.length === 1 ) {
        const country = selectedCountries[ 0 ];
        const dp = country.selectedDayParts.map( m => m.name );

        results.push( ( <span key={ `c_dp_${ country.id }` } css={ styles.displayGroupSpace }>{ dp.join( ", " ) }</span> ) );
      } else {
        for ( const country of selectedCountries ) {
          const dp = country.selectedDayParts.map( m => m.name );

          results.push( ( <div key={ `c_dp_${ country.id }` }><span css={ styles.country }>{ country.name }</span><span css={ styles.displayGroupSpace }>{ dp.join( ", " ) }</span></div> ) );
        }
      }
    } else {
      results.push( ( <span css={ styles.displayGroupSpace } key={ "times" }>{ fromTime } - { toTime }</span> ) );
    }

    return results;
  }, [ days, duration, fromDate, fromTime, selectedCountries, toDate, toTime, useDayParts ] );

  if ( warningMessage ) return <span css={ styles.warning }>{ warningMessage }</span>;
  if ( parts.length > 0 ) return <>{ parts }</>;
  return <></>;
};
